import React, { useState, useEffect } from 'react';
import { ITEMS } from '../data/items';
import { useGameStore, useGameActions } from '../hooks/useGameStore';

const isImageIcon = (icon: string) => icon.startsWith('/');

const ItemIcon: React.FC<{ icon: string; size: string }> = ({ icon, size }) => {
  if (isImageIcon(icon)) {
    return (
      <img
        src={icon}
        alt=""
        style={{
          width: size,
          height: size,
          objectFit: 'cover',
          borderRadius: '4px',
          display: 'block',
          margin: '0 auto',
        }}
      />
    );
  }
  return <span style={{ fontSize: size }}>{icon}</span>;
};

export const Inventory: React.FC = () => {
  const state = useGameStore();
  const actions = useGameActions();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [isAcquired, setIsAcquired] = useState(false);

  // acquiredItem が設定されたら、そのアイテムの詳細モーダルを自動表示
  useEffect(() => {
    if (state.acquiredItem) {
      setSelectedItem(state.acquiredItem);
      setIsAcquired(true);
    }
  }, [state.acquiredItem]);

  const closeDetail = () => {
    if (isAcquired) {
      actions.dismissAcquiredItem();
      setIsAcquired(false);
    }
    setSelectedItem(null);
  };

  // 表示するアイテムID（selectedItem）
  const displayItemId = selectedItem;
  const displayItem = displayItemId ? ITEMS[displayItemId as keyof typeof ITEMS] : null;

  return (
    <>
      {/* Toggle button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: 'fixed',
          bottom: '1rem',
          left: '1rem',
          zIndex: 50,
          background: 'rgba(0,0,0,0.8)',
          border: '1px solid #444',
          color: '#aaa',
          padding: '0.6rem 1rem',
          borderRadius: '4px',
          cursor: 'pointer',
          fontFamily: '"Noto Serif JP", serif',
          fontSize: '1rem',
          letterSpacing: '0.1em',
        }}
      >
        持ち物 ({state.inventory.length})
      </button>

      {/* Inventory panel - icons only */}
      {isOpen && (
        <div style={{
          position: 'fixed',
          bottom: '3.5rem',
          left: '1rem',
          zIndex: 50,
          background: 'rgba(10, 10, 15, 0.95)',
          border: '1px solid #333',
          borderRadius: '8px',
          padding: '1.2rem',
          maxWidth: '720px',
          width: 'calc(100vw - 2rem)',
          maxHeight: '90vh',
          overflow: 'auto',
          fontFamily: '"Noto Serif JP", serif',
        }}>
          <h3 style={{
            color: '#888',
            fontSize: '1rem',
            marginBottom: '0.8rem',
            borderBottom: '1px solid #333',
            paddingBottom: '0.5rem',
            letterSpacing: '0.2em',
          }}>
            持ち物
          </h3>

          {state.inventory.length === 0 ? (
            <p style={{ color: '#555', fontSize: '0.95rem' }}>
              何も持っていない
            </p>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
              gap: '0.6rem',
            }}>
              {state.inventory.map(itemId => {
                const item = ITEMS[itemId];
                if (!item) return null;
                return (
                  <div
                    key={itemId}
                    onClick={() => { setSelectedItem(itemId); setIsAcquired(false); }}
                    style={{
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid #333',
                      borderRadius: '4px',
                      padding: '0.5rem',
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.2s',
                    }}
                  >
                    <div style={{
                      height: '2rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <ItemIcon icon={item.icon} size="1.8rem" />
                    </div>
                    <p style={{
                      color: '#aaa',
                      fontSize: '0.75rem',
                      marginTop: '0.3rem',
                      lineHeight: 1.3,
                    }}>
                      {item.name}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Item detail modal - centered overlay */}
      {displayItem && (() => {
        return (
          <div
            onClick={closeDetail}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 200,
              padding: '2rem',
              cursor: 'pointer',
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: 'linear-gradient(180deg, #1a1a1a 0%, #111 100%)',
                border: '1px solid #444',
                borderRadius: '8px',
                padding: 'clamp(1.5rem, 5vw, 2.5rem)',
                maxWidth: displayItemId === 'morse_chart' ? '650px' : '450px',
                width: '100%',
                textAlign: 'center',
                fontFamily: '"Noto Serif JP", serif',
              }}
            >
              {isAcquired && (
                <p style={{
                  color: '#c8a87c',
                  fontSize: '1rem',
                  letterSpacing: '0.3em',
                  marginBottom: '1.2rem',
                }}>
                  ― 持ち物に追加された ―
                </p>
              )}

              {isImageIcon(displayItem.icon) ? (
                <img
                  src={displayItem.icon}
                  alt={displayItem.name}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '250px',
                    objectFit: 'contain',
                    borderRadius: '6px',
                    border: '1px solid #333',
                  }}
                />
              ) : (
                <span style={{ fontSize: 'clamp(3.5rem, 12vw, 5rem)' }}>
                  {displayItem.icon}
                </span>
              )}
              <h2 style={{
                color: '#c8a87c',
                fontSize: 'clamp(1.2rem, 3.5vw, 1.5rem)',
                marginTop: '1rem',
                marginBottom: '1rem',
                letterSpacing: '0.2em',
                borderBottom: '1px solid #333',
                paddingBottom: '0.8rem',
              }}>
                {displayItem.name}
              </h2>
              <p style={{
                color: '#bbb',
                fontSize: 'clamp(1rem, 3vw, 1.2rem)',
                lineHeight: 2,
                whiteSpace: 'pre-line',
              }}>
                {displayItem.description}
              </p>

              {displayItemId === 'morse_chart' && (
                <div style={{
                  overflowX: 'auto',
                  marginTop: '1.2rem',
                  textAlign: 'left',
                }}>
                  <table style={{
                    borderCollapse: 'collapse',
                    width: '100%',
                    fontSize: 'clamp(0.75rem, 2.5vw, 0.95rem)',
                    fontFamily: 'monospace',
                  }}>
                    <thead>
                      <tr>
                        {['', 'あ段', 'い段', 'う段', 'え段', 'お段'].map((h, i) => (
                          <th key={i} style={{
                            border: '1px solid #444',
                            padding: '0.3rem 0.5rem',
                            color: '#c8a87c',
                            background: 'rgba(200, 168, 124, 0.1)',
                            whiteSpace: 'nowrap',
                          }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ['あ行', '・・', '・ー', 'ー・', '・・ー', '・ー・'],
                        ['か行', '・ーー', 'ー・・', 'ー・ー', '・・・', 'ーー・'],
                        ['さ行', 'ーーー', '・・・・', '・ー・・', '・・ー・', '・・ーー'],
                        ['た行', '・・・ー', '・ー・ー', '・ーー・', 'ーー', '・ーーー'],
                        ['な行', 'ー・・・', 'ー・・ー', 'ー・ー・', 'ー・ーー', 'ーー・・'],
                        ['は行', 'ーー・ー', 'ーーー・', 'ーーーー', '・・・・・', '・・・・ー'],
                        ['ま行', '・・・ー・', '・・・ーー', '・・ー・・', '・・ー・ー', '・・ーー・'],
                        ['や行', '・・ーーー', '', '・ー・・・', '', '・ー・・ー'],
                        ['ら行', '・ー・ー・', '・ー・ーー', '・ーー・・', '・ーー・ー', '・ーーー・'],
                        ['わ行', 'ー・・・・', '', '', '', 'ー・・・ー'],
                        ['', 'ー・・ー・', '', '', '', ''],
                      ].map((row, ri) => (
                        <tr key={ri}>
                          {row.map((cell, ci) => (
                            <td key={ci} style={{
                              border: '1px solid #333',
                              padding: '0.3rem 0.5rem',
                              color: ci === 0 ? '#c8a87c' : '#bbb',
                              background: ci === 0 ? 'rgba(200, 168, 124, 0.1)' : 'transparent',
                              textAlign: 'center',
                              whiteSpace: 'nowrap',
                            }}>
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <button
                onClick={closeDetail}
                style={{
                  marginTop: '1.5rem',
                  padding: '0.6rem 2rem',
                  fontSize: '1.05rem',
                  background: 'transparent',
                  border: '1px solid #555',
                  color: '#888',
                  cursor: 'pointer',
                  fontFamily: '"Noto Serif JP", serif',
                  letterSpacing: '0.2em',
                  borderRadius: '4px',
                  transition: 'all 0.2s',
                }}
              >
                閉じる
              </button>
            </div>
          </div>
        );
      })()}
    </>
  );
};
